// Diatonic chord-tone spelling: each voiced tone gets its OWN letter, with
// whatever accidental that letter needs to land on the right pitch.
//
// The engine names notes from a sharp chromatic scale (fretboard maths), which
// is fine for computing and wrong for reading: an E♭7 comes out "G C C♯ F" —
// a C♯ in a chord that has no C at all, and two tones sharing the letter C.
// A pitch-class-to-flat table is not the fix either; it would give D♭ here but
// still spell a diminished 7th's ♭♭7 as a 6th.
//
// No heuristics are needed, because the catalogue already authors the answer:
// every `ChordType.formula` names its tones by DEGREE ("1 ♭3 ♭5 ♭♭7"), in the
// same order as `intervals`. The degree fixes the letter; the pitch class
// fixes the accidental. So the speller is a lookup, not a guess, and a chord
// whose formula says ♭♭7 gets a doubly-flatted seventh because that is what it
// was declared to be.

import { CHORD_TYPES, type ChordTypeId } from './chords';
import { pitchClassOf, type PitchClass } from './notes';

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
/** Pitch class of each natural letter, indexed as LETTERS. */
const LETTER_PC = [0, 2, 4, 5, 7, 9, 11];

/** Letter steps above the root for a scale degree. Compound degrees (9, 11,
 * 13) are their simple form — the octave is carried by the pitch, not the
 * letter, since a 9th and a 2nd are the same letter. */
const DEGREE_STEPS: Record<number, number> = {
  1: 0,
  // 8 is the octave the triad tier writes as its fourth voice: the SAME
  // letter as the root, an octave up, which is exactly what a doubled root
  // is. (Unlike 9/11/13, whose octave is carried by the pitch alone.)
  8: 0,
  2: 1,
  3: 2,
  4: 3,
  5: 4,
  6: 5,
  7: 6,
  9: 1,
  11: 3,
  13: 5,
};

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Render an accidental offset in semitones as its symbol (ASCII, matching
 * the engine's existing note names — "Bb", "F#", "Bbb"). */
function accidental(offset: number): string {
  if (offset === 0) return '';
  return offset > 0 ? '#'.repeat(offset) : 'b'.repeat(-offset);
}

/** Split a note name into its letter index and accidental offset. */
function parseNoteName(name: string): { letter: number; alter: number } | null {
  const match = name.trim().match(/^([A-Ga-g])([#b♯♭x]*)$/);
  if (!match) return null;
  const letter = LETTERS.indexOf(match[1].toUpperCase() as (typeof LETTERS)[number]);
  if (letter < 0) return null;
  let alter = 0;
  for (const character of match[2]) {
    if (character === '#' || character === '♯') alter += 1;
    else if (character === 'b' || character === '♭') alter -= 1;
    else if (character === 'x') alter += 2;
  }
  return { letter, alter };
}

/** The degree number a formula token names: "♭♭7" → 7, "13" → 13. */
function degreeOf(token: string): number | null {
  const digits = token.match(/\d+/);
  return digits ? Number(digits[0]) : null;
}

/**
 * Spell a chord's voiced tones diatonically, in the catalogue's interval
 * order — E♭7 → ["Eb", "G", "Bb", "Db"], C°7 → ["C", "Eb", "Gb", "Bbb"].
 *
 * `rootDisplay` is the root AS WRITTEN, and it matters: this is a spelling
 * function, so asking it for "A#7" correctly answers A♯ C𝄪 E♯ G♯ where "Bb7"
 * answers B♭ D F A♭. Pass the chart's display root, not the engine's internal
 * sharp name, or a flat-key tune reads in double sharps.
 *
 * Returns null when the root cannot be parsed or the catalogue formula does
 * not line up with the intervals — the caller keeps its existing names rather
 * than showing something invented.
 */
export function spellChordTones(rootDisplay: string, typeId: ChordTypeId): string[] | null {
  const type = CHORD_TYPES[typeId];
  if (!type) return null;
  const root = parseNoteName(rootDisplay);
  if (!root) return null;
  const tokens = type.formula.trim().split(/\s+/);
  if (tokens.length !== type.intervals.length) return null;

  const rootPc = pitchClassOf(rootDisplay);
  const out: string[] = [];
  for (const [index, interval] of type.intervals.entries()) {
    const degree = degreeOf(tokens[index]);
    const steps = degree === null ? null : DEGREE_STEPS[degree];
    if (steps === undefined || steps === null) return null;
    const letter = mod(root.letter + steps, 7);
    const targetPc = mod(rootPc + interval, 12);
    // The accidental is whatever gets this letter to the sounding pitch; fold
    // into −2..+2 so a wrap (B against C♭, say) reads as a flat, not a +11.
    let alter = mod(targetPc - LETTER_PC[letter], 12);
    if (alter > 6) alter -= 12;
    if (Math.abs(alter) > 2) return null;
    out.push(LETTERS[letter] + accidental(alter));
  }
  return out;
}

/**
 * Scientific octave number for a sounding pitch under a given spelling. The
 * octave belongs to the LETTER, not the pitch: B3 spelled C♭ is C♭4, because
 * it is the C above that the flat is lowering. Naming it C♭3 puts the note an
 * octave from where a reader would look for it.
 */
export function spelledOctave(midi: number, name: string): number {
  const parsed = parseNoteName(name);
  if (!parsed) return Math.floor(midi / 12) - 1;
  return Math.floor((midi - parsed.alter) / 12) - 1;
}

/**
 * Letter steps above the root for a chromatic interval, most idiomatic first.
 * Used only for tones the catalogue formula does not name — the added 9ths and
 * 13ths a rootless piano stack introduces, which are real notes in the voicing
 * but not in the chord type's four intervals. The alternates exist so a tone
 * can step aside when its preferred letter is already taken.
 */
const INTERVAL_STEPS: Record<number, number[]> = {
  0: [0, 1],
  1: [1, 0], // ♭9
  2: [1, 2], // 9
  3: [2, 1], // ♭3, else ♯9
  4: [2, 3], // 3
  5: [3, 2], // 11
  6: [3, 4], // ♯11, else ♭5
  7: [4, 3], // 5
  8: [5, 4], // ♭13, else ♯5
  9: [5, 6], // 13
  10: [6, 5], // ♭7
  11: [6, 0], // 7
};

/**
 * Spell the pitches of a voicing, low→high, diatonically against its chord.
 *
 * Catalogue tones take their formula-declared degree (so a °7's ♭♭7 stays a
 * seventh). Anything else the voicing sounds — the added 9 and 13 that make a
 * rootless stack rootless — is spelled by the interval table, taking the first
 * letter not already used. Without that second pass those tones fell back to
 * sharp names and put an A♯ inside an A♭7.
 */
export function spellVoicingMidis(
  midis: number[],
  rootDisplay: string,
  typeId: ChordTypeId,
  fallback: (pc: PitchClass) => string,
): string[] {
  const root = parseNoteName(rootDisplay);
  if (!root) return midis.map((midi) => fallback(mod(midi, 12)));
  const rootPc = pitchClassOf(rootDisplay);
  const byPc = new Map<number, string>();
  const usedLetters = new Set<number>();
  const soundingPcs = new Set(midis.map((midi) => mod(midi, 12)));

  for (const tone of spellChordTones(rootDisplay, typeId) ?? []) {
    byPc.set(pitchClassOf(tone), tone);
    // Only a tone that actually SOUNDS reserves its letter. A D7♭9 upper
    // structure never sounds its ♭9, and letting the silent E♭ hold letter E
    // pushed the sounding ♯9 off its honest E♯ onto a fallback F — a letter
    // the F♯ third already owns.
    if (!soundingPcs.has(pitchClassOf(tone))) continue;
    const parsed = parseNoteName(tone);
    if (parsed) usedLetters.add(parsed.letter);
  }

  // Ascending, so the assignment is deterministic rather than voicing-order
  // dependent: the same chord spells the same way in every rotation.
  const extra = [...new Set(midis.map((midi) => mod(midi, 12)))]
    .filter((pc) => !byPc.has(pc))
    .sort((a, b) => mod(a - rootPc, 12) - mod(b - rootPc, 12));
  for (const pc of extra) {
    const candidates = INTERVAL_STEPS[mod(pc - rootPc, 12)] ?? [];
    let named: string | null = null;
    for (const steps of candidates) {
      const letter = mod(root.letter + steps, 7);
      if (usedLetters.has(letter)) continue;
      let alter = mod(pc - LETTER_PC[letter], 12);
      if (alter > 6) alter -= 12;
      if (Math.abs(alter) > 2) continue;
      usedLetters.add(letter);
      named = LETTERS[letter] + accidental(alter);
      break;
    }
    byPc.set(pc, named ?? fallback(pc));
  }

  return midis.map((midi) => byPc.get(mod(midi, 12)) ?? fallback(mod(midi, 12)));
}
